/*
  Every grading of one card (§6.2–§6.5, §7.4), rebuilt on the task-6 primitives.

  It used to hold its own reducer, its own MUI Paper/Table/Skeleton, its own slice-by-page
  and a `count` kept in a useState synchronised by an effect. All of that now comes from
  components/DataTable, so what is left here is the columns.

  Four things a reader should not have to reconstruct:

  - **The columns are built OUTSIDE this component, and one list serves two views.**
    CardView and CardGradeView both fetch their own gradings and both feed a second
    consumer — FinishTable, which pivots the same filtered rows the table pages. So the
    view owns useTableState and selectTableRows (task 7's shape, because there is a second
    consumer) and this component renders what it is handed. `cardViewColumns` is a frozen
    constant rather than task 7's `<name>Columns(...)` factory for one reason: it takes no
    arguments — the Grade # link is `/report/:gradedId` and needs no route params — so
    there is nothing to memoise it on and a module constant has a stable identity for free.

  - **The sub-grade columns are `numeric`, so they are right-aligned and en-US formatted**
    (§6.2, §12.16). They were `align: "center"` and raw. numeric is for numbers that are
    counted or scored — NOT for card numbers, which are strings (see SetViewTable).

  - **An Authentic grading has no sub-grades, and its sortValue says so.** The cell is
    blank, so the value the column orders by is empty rather than 0 — sortRows partitions
    empties out and appends them in BOTH directions, so a blank cell never sorts as though
    it were the worst possible score.

  - **Every column sorts** (§12.18). The old table sorted through its own comparator, which
    returned an ascending localeCompare for `order: "desc"` — the string columns (Grade #,
    Final) did not reverse when the arrow did. Ordering is sortRowsByColumn's alone now.
*/

import {
  DataTable,
  DataTableColumn,
  DataTableEmpty,
  DataTablePagination,
  TableController,
  TableRowsResult,
} from "components/DataTable";
import GradeReportLink from "components/GradeReportLink";

import { formatCount } from "lib";
import { GRADE_NAMES } from "lib/population";

import { GradingSearchResultDto } from "types/v2/gradings";

type Grading = GradingSearchResultDto;

/*
  An Authentic slab is graded but not scored: the four sub-grade cells are blank on it,
  exactly as they were before the rebuild.
*/
const isAuthentic = (row: Grading): boolean => row.grade === "Authentic";

/* A score that is absent reads as blank, not as a zero the card did not earn. */
const scoreText = (value: number | null | undefined): string =>
  typeof value === "number" ? formatCount(value) : "";

const subGradeColumn = (
  id: string,
  label: string,
  getScore: (row: Grading) => number
): DataTableColumn<Grading> => ({
  id,
  label,
  /* A number: right-aligned, en-US formatted, and highest-first on the first click. */
  numeric: true,
  /* Blank on an Authentic row, so the value is empty there and sorts last both ways. */
  sortValue: (row) => (isAuthentic(row) ? null : getScore(row)),
  render: (row) => (isAuthentic(row) ? "" : scoreText(getScore(row))),
  filterText: (row) => (isAuthentic(row) ? "" : scoreText(getScore(row))),
  className: "w-24",
});

/*
  The final grade as the visitor reads it: "Authentic", or "9.5 (Gem Mint)".

  A slab whose grade NAME is missing shows the score alone. Live data has rows with
  `grade: null` on it (a 6 on MEGA: MEGA Dream ex #204/193, seen at build time) and the
  old template painted them as the literal "6 (null)".
*/
export const finalGradeText = (row: Grading): string => {
  if (isAuthentic(row)) {
    return "Authentic";
  }
  const score = scoreText(row.finalGrade);
  return row.grade ? `${score} (${row.grade})` : score;
};

/*
  How Final orders, best first (Robert, 2026-10-20: "highest graded at the top down to the
  lowest"). The number alone ties every 10, so a Pristine sat among the Gem Mint 10s in
  whatever order the API sent. Three keys, each outweighing everything after it:

    - the final grade (steps of 0.5 are worth 5,000: the name key below reaches 1,600 over
      GRADE_NAMES' 16 entries plus 40 of sub-grades, so it can never cross a grade step);
    - among equal finals, the grade NAME's rank in GRADE_NAMES — Flawless, then Pristine,
      then Gem Mint 10 — at 100 a step, so it outweighs the sub-grade sum (at most 40);
    - then the four sub-grades added together, so a 10/10/10/9.5 Gem Mint 10 leads a
      9.5/9.5/10/9.5 one.

  Authentic stays null, which sorts last in both directions as it always has.
*/
const GRADE_NAME_ORDER = GRADE_NAMES.map((entry) => entry.name);

export const finalGradeRank = (row: Grading): number | null => {
  if (isAuthentic(row) || typeof row.finalGrade !== "number") {
    return null;
  }
  const nameIndex = row.grade ? GRADE_NAME_ORDER.indexOf(row.grade) : -1;
  const nameScore = nameIndex === -1 ? 0 : GRADE_NAME_ORDER.length - nameIndex;
  const subGrades =
    (row.centeringGrade ?? 0) + (row.cornersGrade ?? 0) + (row.edgesGrade ?? 0) + (row.surfaceGrade ?? 0);
  return row.finalGrade * 10000 + nameScore * 100 + subGrades;
};

export const cardViewColumns: readonly DataTableColumn<Grading>[] = [
  {
    id: "gradedId",
    label: "Grade #",
    sortValue: (row) => row.gradedId,
    /* A custom render defaults filterText to "", and the slab number is searchable. */
    filterText: (row) => row.gradedId,
    render: (row) => <GradeReportLink gradedId={row.gradedId} />,
    className: "w-36",
  },
  {
    id: "finalGrade",
    label: "Final",
    /*
      Ordered by the number, not by the rendered string: "10 (Pristine)" against
      "9.5 (Gem Mint)" would collate 10 before 9.5.
    */
    sortValue: finalGradeRank,
    /*
      numeric because the value IS a score — it is what makes the first click show the
      best grades first and the stacked control say "Highest first" rather than "Z to A".
      The alignment is overridden back to left because the CELL is not a bare number:
      "10 (Gem Mint 10)" reads from its left edge like the Grade # beside it.
    */
    numeric: true,
    align: "left",
    filterText: finalGradeText,
    render: (row) => <span>{finalGradeText(row)}</span>,
    className: "w-40",
  },
  subGradeColumn("centeringGrade", "Centering", (row) => row.centeringGrade),
  subGradeColumn("cornersGrade", "Corners", (row) => row.cornersGrade),
  subGradeColumn("edgesGrade", "Edges", (row) => row.edgesGrade),
  subGradeColumn("surfaceGrade", "Surface", (row) => row.surfaceGrade),
];

export interface CardViewTableProps {
  /* The same instance the view passed to selectTableRows. */
  columns: readonly DataTableColumn<Grading>[];
  /*
    The whole result of selectTableRows, not just its rows: pagination has to be given
    view.page (clamped) and view.filteredCount (post-filter). Taking the result object is
    what makes handing it state.page instead — §6.5's blank-page bug — impossible.
  */
  view: TableRowsResult<Grading>;
  table: TableController;
  isLoading: boolean;
  /* RTK Query's error, straight through. Both views used to discard it (§12.20). */
  error?: unknown;
}

function CardViewTable(props: CardViewTableProps) {
  const { columns, view, table, isLoading, error } = props;

  return (
    <div className="flex flex-col">
      <DataTable
        columns={columns}
        rows={view.rows}
        getRowKey={(row) => row.gradedId}
        caption="Gradings"
        sort={table.state.sort}
        onSortChange={table.setSort}
        isLoading={isLoading}
        error={error}
        emptyState={
          <DataTableEmpty
            title="No gradings found"
            description="No grading of this card matches the current search."
          />
        }
        /* Page-sized, so the skeleton is the shape the real page will be. */
        skeletonRows={table.state.rowsPerPage}
      />
      <DataTablePagination
        page={view.page}
        rowsPerPage={table.state.rowsPerPage}
        count={view.filteredCount}
        onChangePage={table.setPage}
        onChangeRowsPerPage={table.setRowsPerPage}
        label="Gradings pagination"
      />
    </div>
  );
}

export default CardViewTable;
